import { v4 as uuidv4 } from 'uuid';

import {
  AddScopeMessage,
  CreateNewConversation,
  RemoveScopeMessage,
  ReplaceScopeMessage,
  SetAuthoringDefaultState,
  SetConversationDefaultState,
  SetConversationFilters,
  SetConversationScope,
  SetCurrentConversationName,
  SetIsNewConversation,
  SetIsResearchModeOn,
  UpdateMessageReferences,
  UpdateSystemMessageTranslationData,
} from '~/constants/actions';
import {
  CHUNK_TYPE,
  DEFAULT_COLLECTION,
  EMPTY_CHAT,
  ERROR_MESSAGE,
  INITIAL_CONVERSATION,
  messageType,
  RESPONSE_TYPE,
  Role,
  WELCOME_AUTHORING_MESSAGE,
} from '~/constants/conversationModels';
import {
  GetConversationScope,
  GetCurrentConversation,
  GetCurrentConversationName,
  GetImageGenerationParams,
  GetIsNewConversation,
  GetIsResearchModeOn,
  GetIsTyping,
  GetQueryValue,
  GetUserConversations,
} from '~/constants/getters';
import {
  AddChunk,
  AddMessage,
  PopMessage,
  SetAllUserConversations,
  SetCurrentConversation,
  SetImageGenerationParams,
  SetInitialConversation,
  SetIsMessageLoading,
  SetQueryValue,
  UpdateMessageSentiment,
} from '~/constants/mutations';

import { listConversations } from '~/services/api/conversations';

import store from '~/store'; // Import the Vuex store instance
import { processCitedReferences } from '~/utils/message';

const defaultState = () => {
  return {
    conversations: [],
    isNewConversation: false,
    currentConversation: INITIAL_CONVERSATION,
    currentConversationName: '', //conversationFilters: {},
    isLoading: false,
    queryValue: '',
    conversationScope: {
      breadcrumb: [],
      collection: '',
      isScoped: false,
    },
    isResearchModeOn: false,
    imageGenerationParams: null,
  };
};

const state = defaultState();

const mutations = {
  [AddChunk](state, data) {
    if (state.currentConversation.conversationId !== data.currentConversationId) {
      return;
    }
    const messages = state.currentConversation.messages;
    const messageId = data.messageId;
    let isTyping = true;

    let messageIndex = messages.findIndex((msg) => msg.messageId === messageId);

    if (messageIndex > 0) {
      // Streaming: append chunk to existing message
      const existingMessage = messages[messageIndex];
      let researchData = existingMessage.researchData ? existingMessage.researchData.map((item) => ({ ...item })) : [];
      let messageText = existingMessage.message || '';
      let updatedData = existingMessage.data || [];
      let chunks = existingMessage.chunks ? [...existingMessage.chunks] : [];

      switch (data.responseType) {
        case RESPONSE_TYPE.messages:
          if (data.message?.chunk_type === CHUNK_TYPE.TOOL_USE) {
            const key = data.message?.node_name || CHUNK_TYPE.RESEARCH;
            const chunkContent = (data.message?.chunk || '').trim();

            // Update researchData
            let entry = researchData.find((item) => item.key === key);
            if (entry) {
              entry.value += chunkContent;
            } else {
              researchData.push({ key, value: chunkContent });
            }

            // Append to chunks in order
            const lastChunk = chunks[chunks.length - 1];
            if (lastChunk?.type === CHUNK_TYPE.RESEARCH && lastChunk.data.key === key) {
              lastChunk.data.value += chunkContent;
            } else {
              chunks.push({ type: CHUNK_TYPE.RESEARCH, data: { key, value: chunkContent } });
            }
          } else {
            const chunkContent = data.message?.chunk || '';
            messageText += chunkContent; // keep legacy message

            const lastChunk = chunks[chunks.length - 1];
            if (lastChunk?.type === CHUNK_TYPE.TEXT) {
              lastChunk.content += chunkContent;
            } else {
              chunks.push({ type: CHUNK_TYPE.TEXT, content: chunkContent });
            }
          }
          break;

        case RESPONSE_TYPE.data: {
          const blockData = data.data || [];
          const documentListBlock = blockData.find((block) => block.type === 'documentList');
          if (documentListBlock) {
            updatedData = processCitedReferences(
              chunks.map((c) => (c.type === CHUNK_TYPE.TEXT ? c.content : '')).join(''),
              blockData,
            );
          } else {
            updatedData = [...(existingMessage.data || []), ...blockData];
          }
          isTyping = false;
          break;
        }

        case RESPONSE_TYPE.systemMessages:
          if (data.message) {
            messageText += data.message;
            const lastChunk = chunks[chunks.length - 1];
            if (lastChunk?.type === CHUNK_TYPE.TEXT) {
              lastChunk.content += data.message;
            } else {
              chunks.push({ type: CHUNK_TYPE.TEXT, content: data.message });
            }
          }
          state.isLoading = false;
          isTyping = false;
          break;

        case RESPONSE_TYPE.endMessage:
          state.isLoading = false;
          isTyping = false;
          break;

        case RESPONSE_TYPE.error:
          state.isLoading = false;
          isTyping = false;
          messageText = ERROR_MESSAGE;
          chunks.push({ type: CHUNK_TYPE.TEXT, content: ERROR_MESSAGE });
          break;

        default:
          console.log('Invalid type of message');
      }

      messages[messageIndex] = {
        ...existingMessage,
        message: messageText,
        isTyping,
        data: updatedData,
        messageType: data.messageType,
        researchData,
        chunks,
      };
    } else {
      // New message (user-sent or initial)
      state.isLoading = false;

      const content =
        RESPONSE_TYPE.systemMessages === data.responseType ? data.message : data.message?.chunk || data.message || ''; // handle non-chunk messages

      const chunks = [];
      if (content) {
        chunks.push({ type: CHUNK_TYPE.TEXT, content });
      }

      const newMessage = {
        ...data,
        role: data.role || Role.chatbot,
        message: content, // keep legacy field
        researchData: [],
        chunks,
        data: data.data || [],
        messageType: data.messageType,
        isTyping: RESPONSE_TYPE.systemMessages !== data.responseType,
      };

      state.currentConversation = {
        ...state.currentConversation,
        messages: [...messages, newMessage],
      };
    }
  },

  [AddMessage](
    state,
    {
      data,
      currentCollection,
      responseMessageId,
      versionContentId,
      promptCardVariables,
      promptCardTitle,
      filters,
      remixImageId,
    },
  ) {
    const messagesToAdd = [];

    // Add user message if `data` is present (authoring cases may skip it)
    if (data) {
      messagesToAdd.push({
        ...EMPTY_CHAT,
        messageId: uuidv4(), // generate a new id for user message
        message: data,
        isUser: true,
        role: Role.user,
        ...currentCollection,
        versionContentId,
        promptCardVariables,
        promptCardTitle,
        filters,
        ...(remixImageId && {
          data: [
            {
              type: 'imageBlock',
              image_ids: [remixImageId],
            },
          ],
        }), // add image_ids to user message if remixImageId is present
      });
    }

    // Add a placeholder message to receive streamed chunks
    messagesToAdd.push({
      ...EMPTY_CHAT,
      messageId: responseMessageId, // used to match future chunks
      isTyping: true,
      ...currentCollection,
    });
    // Update current conversation with new messages
    state.currentConversation = {
      ...state.currentConversation,
      messages: [...state.currentConversation.messages, ...messagesToAdd],
    };
  },

  [UpdateMessageSentiment](state, { messageId, sentiment }) {
    const messages = state.currentConversation.messages;
    const targetIndex = messages.findIndex((msg) => msg.messageId === messageId);

    if (targetIndex === -1) {
      return;
    }

    messages[targetIndex] = { ...messages[targetIndex], liked: sentiment };
  },

  [UpdateMessageReferences](state, { messageId, data }) {
    const messages = state.currentConversation.messages;
    const targetIndex = messages.findIndex((msg) => msg.messageId === messageId);

    if (targetIndex === -1) {
      return;
    }
    messages[targetIndex] = {
      ...messages[targetIndex],
      data: [data],
    };
  },

  [UpdateSystemMessageTranslationData](state, { messageId, data }) {
    const messageIndex = state.currentConversation.messages.findIndex((message) => message.messageId === messageId);

    if (messageIndex < 0) {
      return;
    }
    state.currentConversation.messages[messageIndex] = {
      ...state.currentConversation.messages[messageIndex],
      data,
    };
  },

  [SetConversationScope](state, data) {
    state.conversationScope = {
      ...state.conversationScope,
      ...data,
    };
  },

  // this is used to add the scoped message, will render ScopedMessage Component instead of Message
  [AddScopeMessage](state, message) {
    state.currentConversation = {
      ...state.currentConversation,
      messages: [
        ...state.currentConversation.messages,
        {
          ...message,
          messageType: messageType.context, // scopedMessage: true,
        },
      ],
    };
  },
  [ReplaceScopeMessage](state, message) {
    const newMessages = [...state.currentConversation.messages];
    newMessages[newMessages.length - 1] = {
      ...message,
      messageType: messageType.context,
    };
    state.currentConversation.messages = newMessages;
  },

  [RemoveScopeMessage](state) {
    state.currentConversation.messages.pop();
  },

  [SetCurrentConversation](state, conversation) {
    state.currentConversation = conversation;
  },
  [SetCurrentConversationName](state, data) {
    const currentConversationIndex = state.conversations.findIndex(
      (conv) => conv.conversationId === data.conversationId,
    );

    state.currentConversationName = data.new_title || '';
    if (currentConversationIndex > -1) {
      state.conversations[currentConversationIndex] = {
        ...state.conversations[currentConversationIndex],
        title: data.new_title || '',
      };
    }
  },
  [SetQueryValue](state, value) {
    state.queryValue = value;
  },
  [SetIsMessageLoading](state, isLoading) {
    state.isLoading = isLoading;
  },

  [SetAllUserConversations](state, data) {
    state.conversations = data;
  },
  [CreateNewConversation](state, { conversationId, isAuthoring = false, userId }) {
    state.currentConversation = {
      ...state.currentConversation,
      conversationId,
      userId,
      timestamp: Date.now(),
      messages: isAuthoring ? [WELCOME_AUTHORING_MESSAGE] : [],
    };
    state.conversations = [state.currentConversation, ...state.conversations];
  },
  [SetConversationFilters](state, { key, value }) {
    if (!value) {
      delete state.conversationFilters[key];
    } else {
      state.conversationFilters = { ...state.conversationFilters, [key]: value };
    }
  },
  [SetIsNewConversation](state, data) {
    state.isNewConversation = data;
  },
  [SetConversationDefaultState](state) {
    store.dispatch(`authoringStore/${SetAuthoringDefaultState}`);
    Object.assign(state, defaultState());
  },
  [SetIsResearchModeOn](state, data) {
    state.isResearchModeOn = data;
  },
  [SetImageGenerationParams](state, data) {
    state.imageGenerationParams = data;
  },
  [PopMessage](state, messageId) {
    const messages = state.currentConversation.messages;
    const index = messages.findIndex((msg) => msg.messageId === messageId);
    const messageObject = messages[index];

    if (index !== -1) {
      const newMessage = {
        ...messageObject,
        message: '',
        chunks: [],
        data: [],
        researchData: [],
        isTyping: true,
      };

      // Replace the message in place and update the array reference for reactivity
      messages[index] = newMessage;
      state.currentConversation.messages = [...messages];
    }
  },
};

const getters = {
  [GetCurrentConversation](state) {
    return state.currentConversation;
  },
  [GetCurrentConversationName](state) {
    return state.currentConversationName;
  },
  [GetUserConversations](state) {
    return state.conversations;
  },
  [GetConversationScope](state) {
    return state.conversationScope;
  },
  [GetIsTyping](state) {
    return state.isLoading;
  },
  [GetQueryValue](state) {
    return state.queryValue;
  },
  [GetIsNewConversation](state) {
    return state.isNewConversation;
  },
  [GetIsResearchModeOn](state) {
    return state.isResearchModeOn;
  },
  [GetImageGenerationParams](state) {
    return state.imageGenerationParams;
  },
};

const actions = {
  [AddMessage]: ({ commit }, data) => {
    commit(AddMessage, data);
    commit(SetIsMessageLoading, true);
  },
  [AddChunk]: async ({ commit }, data) => {
    commit(AddChunk, data);
  },
  [SetCurrentConversation]: async ({ commit, state }, data) => {
    if (state.isLoading) {
      commit(SetIsMessageLoading, false);
    }
    commit(SetCurrentConversation, data);
  },

  [SetInitialConversation]: ({ commit }) => {
    commit(SetIsMessageLoading, false);
    commit(SetCurrentConversation, {
      ...INITIAL_CONVERSATION,
    });
  },
  [SetCurrentConversationName]: async ({ commit }, data) => {
    commit(SetCurrentConversationName, data);
  },
  [SetConversationScope]: ({ commit, state }, data) => {
    const lastMessage = state.currentConversation.messages[state.currentConversation.messages.length - 1];
    const prevLastMessage = state.currentConversation.messages[state.currentConversation.messages.length - 2];
    let { collection, isScoped } = data;

    // need this in case scope does not change, breadcrumbs changes in collectionsPanelDialog
    if (!collection) {
      commit(SetConversationScope, data);
      return;
    }

    // Corner Cases
    // no messages but scoped
    // this case is needed when user creates a new conversation and he scoped that

    if (!lastMessage && isScoped) {
      commit(AddScopeMessage, collection);
      commit(SetConversationScope, data);
      return;
    } else if (state.currentConversation.messages.length === 1 && !isScoped) {
      commit(RemoveScopeMessage);
      commit(SetConversationScope, data);
      return;
    } else if (!lastMessage && !isScoped) {
      commit(SetConversationScope, data);
      return;
    }

    // split in two cases opens collection panel (scope conversation) and close collections panel (back to vault)

    if (isScoped) {
      // add new scope message when last message is a normal message scoped in a different collection than the current
      if (lastMessage.messageType !== messageType.context && lastMessage.collectionId !== collection.collectionId) {
        commit(AddScopeMessage, collection);
        // replace scope message (when the last one is a scope message) , this case appear when user changed collection (new scope)
      } else if (
        lastMessage.messageType === messageType.context &&
        prevLastMessage.collectionId !== collection.collectionId
      ) {
        commit(ReplaceScopeMessage, collection);
        // this is the case when last scope message is 'Now talking to vault' && prevLastMessage.collectionId === collection.collectionId
      } else if (
        lastMessage.messageType === messageType.context &&
        lastMessage.collectionId === DEFAULT_COLLECTION.collectionId
      ) {
        commit(RemoveScopeMessage);
      }
    } else {
      // 3 conditions for default collection (vault) when we close

      // remove last scope message if no message was sent during scoping and the previous message is scoped to vault
      if (
        lastMessage.messageType === messageType.context &&
        prevLastMessage.collectionId === DEFAULT_COLLECTION.collectionId
      ) {
        commit(RemoveScopeMessage);
        // replace last scope message if no message was sent during scoping and the previous message is not scoped to vault
      } else if (
        lastMessage.messageType === messageType.context &&
        prevLastMessage.collectionId !== DEFAULT_COLLECTION.collectionId
      ) {
        commit(ReplaceScopeMessage, DEFAULT_COLLECTION);
        // add scope message ('Now talking to vault') if previous message was not scoped to vault
      } else if (
        lastMessage.messageType !== messageType.context &&
        lastMessage.collectionId !== DEFAULT_COLLECTION.collectionId
      ) {
        commit(AddScopeMessage, DEFAULT_COLLECTION);
      }
    }

    commit(SetConversationScope, data);
  },
  [SetQueryValue]: ({ commit }, value) => {
    commit(SetQueryValue, value);
  },
  [SetIsNewConversation]: ({ commit }, value) => {
    commit(SetIsNewConversation, value);
  },
  [SetAllUserConversations]: async ({ commit }) => {
    try {
      const response = await listConversations();
      commit(SetAllUserConversations, response.data);
    } catch (error) {
      console.error('Error fetching user conversations:', error);
      // Handle error here
    }
  },

  [CreateNewConversation]: ({ commit }, data) => {
    commit(CreateNewConversation, data);
  },
  [UpdateMessageSentiment]: ({ commit }, { messageId, sentiment }) => {
    commit(UpdateMessageSentiment, { messageId, sentiment });
  },
  [UpdateMessageReferences]: ({ commit }, { messageId, data }) => {
    commit(UpdateMessageReferences, { messageId, data });
  },
  [UpdateSystemMessageTranslationData]: ({ commit }, { messageId, data }) => {
    commit(UpdateSystemMessageTranslationData, { messageId, data });
  },
  [SetConversationDefaultState]: ({ commit }) => {
    commit(SetConversationDefaultState);
  },
  [SetIsResearchModeOn]: ({ commit }, value) => {
    commit(SetIsResearchModeOn, value);
  },
  [SetImageGenerationParams]: ({ commit }, value) => {
    commit(SetImageGenerationParams, value);
  },
  [PopMessage]: ({ commit }, messageId) => {
    commit(PopMessage, messageId);
  },
};

export default {
  namespaced: true, // Important for namespacing module
  state,
  mutations,
  actions,
  getters,
};
